/**
 * Auth helpers for guest users and token invalidation.
 * Use getIsLoggedInFromStorage() before actions that require login (inquiry, booking).
 * Use clearAuthAndRedirect() when API returns 401 or token invalid.
 */

const STATUS_KEY = "status";
const SESSION_TOKEN_KEY = "sessionToken";

/**
 * Check if user is logged in using localStorage (sessionToken; status optional).
 * Login/OTP set sessionToken; status may not be set, so we treat token as the source of truth.
 */
export const getIsLoggedInFromStorage = () => {
  try {
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    if (!token) return false;
    const storedStatus = localStorage.getItem(STATUS_KEY);
    const statusOk = storedStatus == null || storedStatus === "" || JSON.parse(storedStatus) === 200;
    return statusOk;
  } catch {
    return Boolean(localStorage.getItem(SESSION_TOKEN_KEY));
  }
};

export const SESSION_LOGGED_IN_ELSEWHERE_MESSAGE =
  "Your profile is logged in on another device or browser. Please sign in again.";

/**
 * Success HTTP body when session was replaced elsewhere (e.g. getProfile code 404 + token invalid).
 */
export function isSessionInvalidApiBody(body) {
  if (!body || typeof body !== "object") return false;
  const code = Number(body.code);
  const msg = (body.message || body.errorMessage || "")
    .toString()
    .toLowerCase();
  const tokenInvalid =
    msg.includes("token") &&
    (msg.includes("invalid") || msg.includes("expired"));
  if (code === 401 || tokenInvalid) return true;
  if (code === 404 && tokenInvalid) return true;
  if (msg.includes("logged in") && msg.includes("another")) return true;
  return false;
}

let sessionInvalidNoticeShown = false;

/**
 * Show relogin modal; clear auth and redirect only after user clicks OK.
 */
export async function clearAuthWithReloginNotice(
  message = SESSION_LOGGED_IN_ELSEWHERE_MESSAGE
) {
  if (sessionInvalidNoticeShown) return;
  sessionInvalidNoticeShown = true;
  const { requestSessionReloginModal } = await import("./sessionReloginModal");
  await requestSessionReloginModal(message);
  clearAuthAndRedirect();
}

/**
 * Clear all auth-related data and redirect to home.
 * Call this when API returns 401 or "token invalid" / "unauthorized".
 */
export const clearAuthAndRedirect = () => {
  try {
    localStorage.removeItem(STATUS_KEY);
    localStorage.removeItem(SESSION_TOKEN_KEY);
    localStorage.clear();
  } catch (e) {
    console.error("Error clearing auth:", e);
  }
  window.location.href = "/";
};

/**
 * Check if an API error response indicates invalid/expired token.
 */
export const isTokenInvalidResponse = (error) => {
  if (!error) return false;
  const data = error?.response?.data;
  if (isSessionInvalidApiBody(data)) return true;
  const status = error?.response?.status;
  const message = (data?.message || data?.errorMessage || error?.message || "")
    .toString()
    .toLowerCase();
  if (status === 401) return true;
  if (
    message.includes("token") &&
    (message.includes("invalid") ||
      message.includes("expired") ||
      message.includes("unauthorized"))
  ) {
    return true;
  }
  if (message.includes("unauthorized")) return true;
  return false;
};
